import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Filter,
  FileText,
  Award,
  Star,
  Crown,
  Zap,
  Target,
  Users,
} from 'lucide-react';
import { nekoStaff } from 'public-contracts';
import { staffBadgeService } from '../services/staffBadgeService';
import { LoadingIndicator } from '../components/common/LoadingIndicator';

type StaffBadge = nekoStaff.StaffBadge;
type BadgeCategory = nekoStaff.BadgeCategory;
type BadgeRarity = nekoStaff.BadgeRarity;

// Import the enum values
const { BadgeCategory: BadgeCategoryEnum, BadgeRarity: BadgeRarityEnum } =
  nekoStaff;

const ModernStaffBadgeList: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { user, fetchUser } = useUserStore();

  const [badges, setBadges] = useState<StaffBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterRarity, setFilterRarity] = useState<string>('all');

  // Ensure user data is loaded
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  const fetchBadges = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await staffBadgeService.getStaffBadges();
      setBadges(response);
    } catch (err) {
      console.error('Error fetching badges:', err);
      // Use mock data instead of showing error
      notify(
        translate('pos.staff.badges.failed_load', 'Failed to load badges'),
        { type: 'error' }
      );

      // Fallback mock data if API fails
      const mockData: StaffBadge[] = [
        {
          id: '1',
          name: 'First Sale',
          description: 'Completed your first sale',
          category: BadgeCategoryEnum.PERFORMANCE,
          rarity: BadgeRarityEnum.COMMON,
          pointReward: 100,
          iconUrl: '🎯',
          criteria: {
            type: 'automatic',
            conditions: [],
          },
          isActive: true,
          sortOrder: 1,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '2',
          name: 'Speed Demon',
          description: 'Processed 50 orders in a single day',
          category: BadgeCategoryEnum.PERFORMANCE,
          rarity: BadgeRarityEnum.RARE,
          pointReward: 500,
          iconUrl: '⚡',
          criteria: {
            type: 'automatic',
            conditions: [],
          },
          isActive: true,
          sortOrder: 2,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '3',
          name: 'Customer Favorite',
          description: 'Received 10 positive customer reviews',
          category: BadgeCategoryEnum.CUSTOMER_SERVICE,
          rarity: BadgeRarityEnum.EPIC,
          pointReward: 1000,
          iconUrl: '⭐',
          criteria: {
            type: 'automatic',
            conditions: [],
          },
          isActive: true,
          sortOrder: 3,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '4',
          name: 'Team Player',
          description: 'Helped 5 different team members',
          category: BadgeCategoryEnum.TEAMWORK,
          rarity: BadgeRarityEnum.COMMON,
          pointReward: 200,
          iconUrl: '👥',
          criteria: {
            type: 'automatic',
            conditions: [],
          },
          isActive: true,
          sortOrder: 4,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          id: '5',
          name: 'Legendary Barista',
          description: 'Mastered all coffee brewing techniques',
          category: BadgeCategoryEnum.SPECIAL,
          rarity: BadgeRarityEnum.LEGENDARY,
          pointReward: 2000,
          iconUrl: '👑',
          criteria: {
            type: 'automatic',
            conditions: [],
          },
          isActive: true,
          sortOrder: 5,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ];
      setBadges(mockData);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  // Load real data
  useEffect(() => {
    fetchBadges();
  }, [fetchBadges]);

  const handleDelete = async (id: string) => {
    if (!confirm(translate('pos.common.message.delete_content'))) return;

    try {
      await staffBadgeService.deleteStaffBadge(id);

      setBadges((prev) => prev.filter((item) => item.id !== id));
      notify(
        translate('pos.staff.badges.delete_success', 'Badge deleted successfully'),
        { type: 'success' }
      );
    } catch (err) {
      console.error('Error deleting badge:', err);
      notify(
        translate('pos.staff.badges.delete_failed', 'Failed to delete badge'),
        { type: 'error' }
      );
    }
  };

  const filteredBadges = useMemo(() => {
    let filtered = badges;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter((item) => item.category === filterCategory);
    }

    // Rarity filter
    if (filterRarity !== 'all') {
      filtered = filtered.filter((item) => item.rarity === filterRarity);
    }

    return filtered;
  }, [badges, searchTerm, filterCategory, filterRarity]);

  const getRarityBadgeVariant = (rarity: BadgeRarity) => {
    switch (rarity) {
      case BadgeRarityEnum.LEGENDARY:
        return 'default';
      case BadgeRarityEnum.EPIC:
        return 'secondary';
      case BadgeRarityEnum.RARE:
        return 'outline';
      case BadgeRarityEnum.UNCOMMON:
        return 'outline';
      case BadgeRarityEnum.COMMON:
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getRarityBadgeColor = (rarity: BadgeRarity) => {
    switch (rarity) {
      case BadgeRarityEnum.LEGENDARY:
        return 'text-purple-600 bg-purple-100';
      case BadgeRarityEnum.EPIC:
        return 'text-blue-600 bg-blue-100';
      case BadgeRarityEnum.RARE:
        return 'text-green-600 bg-green-100';
      case BadgeRarityEnum.UNCOMMON:
        return 'text-yellow-600 bg-yellow-100';
      case BadgeRarityEnum.COMMON:
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category: BadgeCategory) => {
    switch (category) {
      case BadgeCategoryEnum.ATTENDANCE:
        return <Target className="h-4 w-4" />;
      case BadgeCategoryEnum.PERFORMANCE:
        return <Zap className="h-4 w-4" />;
      case BadgeCategoryEnum.CUSTOMER_SERVICE:
        return <Star className="h-4 w-4" />;
      case BadgeCategoryEnum.TEAMWORK:
        return <Users className="h-4 w-4" />;
      case BadgeCategoryEnum.SPECIAL:
        return <Award className="h-4 w-4" />;
      default:
        return <Award className="h-4 w-4" />;
    }
  };

  if (loading) {
    return <LoadingIndicator fullPage />;
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="text-center text-red-600">
            <p>{error}</p>
            <Button onClick={fetchBadges} className="mt-2" variant="outline">
              {translate('pos.common.action.refresh')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {translate('pos.staff.badges.title', 'Badges')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {translate(
              'pos.staff.badges.subtitle',
              'View your achievement badges and rewards'
            )}
          </p>
        </div>
        <Button
          onClick={() => navigate('/staff_badges/create')}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          {translate('pos.staff.common.add', 'Add')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.badges.stats_total_badges', 'Total Badges')}
            </CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{badges.length}</div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.badges.stats_available_badges',
                'Available badges'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate('pos.staff.badges.stats_active_badges', 'Active Badges')}
            </CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {badges.filter((b) => b.isActive).length}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.badges.stats_currently_active',
                'Currently active'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate(
                'pos.staff.badges.stats_legendary_badges',
                'Legendary Badges'
              )}
            </CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {
                badges.filter((b) => b.rarity === BadgeRarityEnum.LEGENDARY)
                  .length
              }
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.badges.stats_highest_rarity',
                'Highest rarity'
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {translate(
                'pos.staff.badges.stats_avg_points',
                'Avg. Points Required'
              )}
            </CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(
                badges.reduce((sum, b) => sum + b.pointReward, 0) /
                  badges.length
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {translate(
                'pos.staff.badges.stats_points_to_earn',
                'Points to earn'
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {translate('pos.staff.badges.filters_title', 'Filters')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="search">
                {translate('pos.staff.badges.search_label', 'Search')}
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  id="search"
                  placeholder={translate(
                    'pos.staff.badges.search_placeholder',
                    'Search badges...'
                  )}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <Label htmlFor="filter-category">
                {translate('pos.staff.badges.category_label', 'Category')}
              </Label>
              <select
                id="filter-category"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                title={translate('pos.staff.badges.category_label', 'Category')}
              >
                <option value="all">
                  {translate('pos.staff.badges.all_categories', 'All Categories')}
                </option>
                <option value={BadgeCategoryEnum.ATTENDANCE}>
                  {translate('pos.staff.badges.attendance', 'Attendance')}
                </option>
                <option value={BadgeCategoryEnum.PERFORMANCE}>
                  {translate('pos.staff.badges.performance', 'Performance')}
                </option>
                <option value={BadgeCategoryEnum.TEAMWORK}>
                  {translate('pos.staff.badges.teamwork', 'Teamwork')}
                </option>
                <option value={BadgeCategoryEnum.SPECIAL}>
                  {translate('pos.staff.badges.special', 'Special')}
                </option>
                <option value={BadgeCategoryEnum.CUSTOMER_SERVICE}>
                  {translate(
                    'pos.staff.badges.customer_service',
                    'Customer Service'
                  )}
                </option>
              </select>
            </div>
            <div className="sm:w-48">
              <Label htmlFor="filter-rarity">
                {translate('pos.staff.badges.rarity_label', 'Rarity')}
              </Label>
              <select
                id="filter-rarity"
                value={filterRarity}
                onChange={(e) => setFilterRarity(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                title={translate('pos.staff.badges.rarity_label', 'Rarity')}
              >
                <option value="all">
                  {translate('pos.staff.badges.all_rarities', 'All Rarities')}
                </option>
                <option value={BadgeRarityEnum.COMMON}>
                  {translate('pos.staff.badges.common', 'Common')}
                </option>
                <option value={BadgeRarityEnum.UNCOMMON}>
                  {translate('pos.staff.badges.uncommon', 'Uncommon')}
                </option>
                <option value={BadgeRarityEnum.RARE}>
                  {translate('pos.staff.badges.rare', 'Rare')}
                </option>
                <option value={BadgeRarityEnum.EPIC}>
                  {translate('pos.staff.badges.epic', 'Epic')}
                </option>
                <option value={BadgeRarityEnum.LEGENDARY}>
                  {translate('pos.staff.badges.legendary', 'Legendary')}
                </option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Badges List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {translate('pos.staff.badges.staff_badges_title', 'Staff Badges')} (
            {translate('pos.staff.badges.items_count', '%{count} items').replace(
              '%{count}',
              `${filteredBadges.length}`
            )}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredBadges.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm ||
                filterCategory !== 'all' ||
                filterRarity !== 'all'
                  ? translate(
                      'pos.staff.badges.no_badges_matching',
                      'No badges found matching your criteria'
                    )
                  : translate('pos.staff.badges.no_badges_found', 'No badges found')}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredBadges.map((badge) => (
                <div
                  key={badge.id}
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">{badge.iconUrl}</div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {badge.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {badge.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(`/staff_badges/${badge.id}/edit`)
                        }
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(badge.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getCategoryIcon(badge.category)}
                      <span className="text-sm text-gray-600 capitalize">
                        {badge.category === BadgeCategoryEnum.ATTENDANCE
                          ? translate('pos.staff.badges.attendance', 'Attendance')
                          : badge.category === BadgeCategoryEnum.PERFORMANCE
                          ? translate('pos.staff.badges.performance', 'Performance')
                          : badge.category === BadgeCategoryEnum.TEAMWORK
                          ? translate('pos.staff.badges.teamwork', 'Teamwork')
                          : badge.category === BadgeCategoryEnum.SPECIAL
                          ? translate('pos.staff.badges.special', 'Special')
                          : translate(
                              'pos.staff.badges.customer_service',
                              'Customer Service'
                            )}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant={getRarityBadgeVariant(badge.rarity)}
                        className={getRarityBadgeColor(badge.rarity)}
                      >
                        {badge.rarity === BadgeRarityEnum.COMMON
                          ? translate('pos.staff.badges.common', 'Common')
                          : badge.rarity === BadgeRarityEnum.UNCOMMON
                          ? translate('pos.staff.badges.uncommon', 'Uncommon')
                          : badge.rarity === BadgeRarityEnum.RARE
                          ? translate('pos.staff.badges.rare', 'Rare')
                          : badge.rarity === BadgeRarityEnum.EPIC
                          ? translate('pos.staff.badges.epic', 'Epic')
                          : translate('pos.staff.badges.legendary', 'Legendary')}
                      </Badge>
                      <span className="text-sm font-medium text-gray-900">
                        {badge.pointReward}{' '}
                        {translate('pos.staff.badges.points_label', 'pts')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModernStaffBadgeList;
